import * as Arr from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Record';
import { flow, pipe } from 'fp-ts/lib/function';
import { iso } from 'newtype-ts';
import { Product } from '..';
import * as Api from '../../api';
import * as Option from './option';
import { fromApiProductProperty } from './configurableProperties/propertyValues';
// provides ability to wrap, unwrap, and modify the newtype
const _ = iso();
// export functions from here
export const empty = () => _.wrap({});
export const create = _.wrap;
// Required for now to allow communication back from front-end generated Selections passed into Api Methods while we write the minimal amount of code possible.
export const toApi = _.unwrap;
export const entries = flow(toApi, s => Object.entries(s));
// TODO: return an Option to avoid bugs where someone disregards a legitimate null value, mistaking it for undefined
export const getByName = (selections, name) => {
    return _.unwrap(selections)[name];
};
export const addValue = (selections, property, value) => {
    const rawSelections = Object.assign({}, _.unwrap(selections));
    rawSelections[property] = value;
    return create(rawSelections);
};
export const addValueNullable = (selections, property, value) => {
    if (selections === null) {
        return create({ [property]: value });
    }
    else {
        return addValue(selections, property, value);
    }
};
export const removeKeys = (selections, keys) => {
    const rawSelections = _.unwrap(selections);
    const prunedSelections = Object.fromEntries(Object.entries(rawSelections).filter(([property]) => !keys.includes(property)));
    return create(prunedSelections);
};
const Eq_ = R.getEq({ equals: (x, y) => x === y });
export const Eq = {
    equals: (a, b) => Eq_.equals(_.unwrap(a), _.unwrap(b)),
};
/** Check if two Selections are equal to each other by value */
export const equals = Eq.equals;
export const keepKeys = (selections, keys) => {
    const rawSelections = _.unwrap(selections);
    const prunedSelections = Object.fromEntries(Object.entries(rawSelections).filter(([property]) => keys.includes(property)));
    return create(prunedSelections);
};
/**
 * Combines. if any Selections appear in both a and b, will keep the value from b
 */
export const concat = (a, b) => {
    return create(Object.assign({}, _.unwrap(a), _.unwrap(b)));
};
/**
 * Concatinates two selections together. If either is null, returns the other unchanged.
 *
 * If any selections appear in both passed arguments, the rightmost value will win.
 */
export const concatNullable = (a, b) => {
    return pipe(O.Do, O.bind('a', () => O.fromNullable(a)), O.bind('b', () => O.fromNullable(b)), O.map(({ a, b }) => concat(a, b)), O.alt(() => O.fromNullable(a)), O.alt(() => O.fromNullable(b)), O.toNullable);
};
// E.g. Option { name: Basin, Value: SKU1 } -> Selections { Basin: SKU1 }
export const fromOption = (option) => {
    return create({ [Option.name(option)]: Option.value(option) });
};
// expands out, throws away the parts, returns each product option in a single selections object
export const fromProductSelectionsByPart = (productSelectionsByPart) => Object.entries(productSelectionsByPart)
    .map(([_part, option]) => option)
    .map(fromOption)
    // Combine each product option into a single selections object
    .reduce((acc, cur) => concat(acc, cur), empty());
export const count = (selections) => {
    return Object.keys(_.unwrap(selections)).length;
};
export const getBuildPropertySelectionsForConfiguration = (configurator, selections, heroPart) => {
    const buildProperties = Api.ConfiguratorDefinition.getBuildProperties(configurator, heroPart);
    const buildSelections = buildProperties.reduce((heroSels, property) => {
        const buildSelection = getByName(selections, property);
        // Careful Now - buildSelection can be null legitimately
        if (buildSelection !== undefined) {
            heroSels = addValue(heroSels, property, buildSelection);
        }
        return heroSels;
    }, empty());
    return buildSelections;
};
export const getPropertySelectionsForProduct = (product) => Object.keys(Product.configurableProperties(product) ?? {}).reduce((sels, propertyKey) => {
    const propertyValue = fromApiProductProperty(propertyKey, Product.properties(product));
    if (O.isSome(propertyValue)) {
        sels = addValue(sels, propertyKey, propertyValue.value.displayText);
    }
    return sels;
}, empty());
export const getGroupingCodeSelections = (selections) => {
    const unwrappedSelections = _.unwrap(selections);
    return Object.entries(unwrappedSelections).reduce((groupingCodeSels, [key, value]) => {
        if (key.includes('GroupingCode')) {
            groupingCodeSels = create({ [key]: value });
        }
        return groupingCodeSels;
    }, empty());
};
/**
 * Constructs a string to be used for caching purposes - ensures an order to the selections
 */
export const toCacheKey = (s) => pipe(s, _.unwrap, Object.entries, (xs) => xs.sort(([a], [b]) => a.localeCompare(b)), Arr.reduce('', (str, [key, value]) => {
    return `${str}:${key}${value}`;
}));
/**
 * Checks if the left Selections is a subset of the right Selections, both keys and values are checked
 */
export const isSubset = (left, right) => {
    const _left = _.unwrap(left);
    const _right = _.unwrap(right);
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    const includes = leftKeys.every(key => rightKeys.includes(key));
    if (includes) {
        return leftKeys.every(key => {
            const leftValue = _left[key];
            const rightValue = _right[key];
            return leftValue === rightValue;
        });
    }
    return false;
};
/** Split a single Selections object into multiple Selections, one per property in the original */
export const split = (selections) => {
    const _selections = _.unwrap(selections);
    return pipe(Object.entries(_selections), Arr.map(entry => Object.fromEntries([entry])), Arr.map(create));
};
